import {
  type AlbumsQueryModelWithPagination,
  createAlbumsQueryModel,
} from '@/entities/albums'
import {
  type AuthorsQueryModelWithPagination,
  createAuthorsQueryModel,
} from '@/entities/authors'
import { type AuthorPreview } from '@/entities/authors'
import {
  TagContentTypeEnum,
  type TagsQueryModelWithPagination,
  createTagsQueryModel,
} from '@/entities/tags'
import {
  trackByIdQuery,
  trackCreate,
  trackToggleArchive,
  trackUpdate,
} from '@/entities/tracks'
import { type UploadFileWithId } from '@/features/upload-files'
import {
  type PutApiTracksIdMutationRequest,
  type TrackAlbumBindModel,
  type TrackAuthorBindModel,
  type TrackModel,
  type TrackTagBindModel,
  type TrackTypeEnumEnum,
  type UpdateTrackModel,
} from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { createModalModel } from '@/shared/lib/model-factories'
import { navigateToFx } from '@/shared/lib/providers'
import { combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { or } from 'patronum'

export type TrackFormModel = UpdateTrackModel & {
  authorId?: string
  coauthorIds?: string[]
  genreIds?: number[]
  files?: UploadFileWithId[]
  picture?: UploadFileWithId[]
  albumPicture?: UploadFileWithId[]
}

type TrackFormGateProps = {
  isModal: boolean
  initialAuthor?: AuthorPreview
  onSubmitted?: (trackId: string) => void
  resetForm?: () => void
}

export const TrackFormGate = createGate<TrackFormGateProps>()

// Form data
const $trackFormTrackData = createStore<TrackModel | null>(null)
export const trackFormTrackDataChanged = createEvent<TrackModel | null>()
$trackFormTrackData
  .on(trackFormTrackDataChanged, (_, trackData) => {
    return trackData
  })
  .reset(TrackFormGate.close)

// ContentType

export const trackFormContentTypeChanged = createEvent<TrackTypeEnumEnum>()
const $trackFormContentType = createStore<TrackTypeEnumEnum | null>(null)
  .on(trackFormContentTypeChanged, (_, contentType) => contentType)
  .reset(TrackFormGate.close)

// Tags
export const tagsTrackFormTagsQueryModel = createTagsQueryModel({
  usePagination: true,
  accumulate: true,
}) as TagsQueryModelWithPagination
const {
  $isAvailable: $tagsTrackFormIsAvailable,
  availableSet: tagsTrackFormAvailableSet,
  unavailableSet: tagsTrackFormUnavailableSet,
} = tagsTrackFormTagsQueryModel
export const {
  //   Query
  query: tagsTrackFormQuery,
  //   Init
  filtersInit: tagsTrackFormFiltersInit,
  // Pagination
  pageIncreased: tagsTrackFormPageIncreased,
  pageSizeChanged: tagsTrackFormPageSizeChanged,
  //   Search
  $search: $tagsTrackFormSearch,
  searchChanged: tagsTrackFormSearchChanged,
  searchReset: tagsTrackFormSearchReset,
  //   Refresh
  refreshed: tagsTrackFormRefreshed,
  // Accumulated
  $accumulatedData: $tagsTrackFormAccumulatedData,
  accumulatedDataReset: tagsTrackFormAccumulatedDataReset,
} = tagsTrackFormTagsQueryModel

sample({
  source: $tagsTrackFormIsAvailable,
  clock: $trackFormContentType,
  filter: (queryIsAvailable, type) => {
    return !queryIsAvailable && Boolean(type)
  },
  target: tagsTrackFormAvailableSet,
})
sample({
  clock: TrackFormGate.close,
  target: [tagsTrackFormUnavailableSet, tagsTrackFormAccumulatedDataReset],
})

export const $trackFormTags = combine(
  $tagsTrackFormAccumulatedData,
  $trackFormTrackData,
  (fetchedTags, track): TrackTagBindModel[] => {
    const combined = [
      ...fetchedTags.map((a) => ({
        id: a.id,
        name: a.name,
      })),
      ...(track && track.genres ? track.genres : []),
    ]

    const uniqueMap = new Map<number, TrackTagBindModel>()
    for (const tag of combined) {
      uniqueMap.set(tag.id, tag)
    }

    return Array.from(uniqueMap.values())
  },
)

// Moods
export const moodsTrackFormTagsQueryModel = createTagsQueryModel({
  initialFilters: { Types: [TagContentTypeEnum.Mood] },
  usePagination: true,
  accumulate: true,
}) as TagsQueryModelWithPagination
const {
  availableSet: moodsTrackFormAvailableSet,
  unavailableSet: moodsTrackFormUnavailableSet,
} = moodsTrackFormTagsQueryModel
export const {
  //   Query
  query: moodsTrackFormQuery,
  //   Init
  filtersInit: moodsTrackFormFiltersInit,
  // Pagination
  pageIncreased: moodsTrackFormPageIncreased,
  pageSizeChanged: moodsTrackFormPageSizeChanged,
  //   Search
  $search: $moodsTrackFormSearch,
  searchChanged: moodsTrackFormSearchChanged,
  searchReset: moodsTrackFormSearchReset,
  //   Refresh
  refreshed: moodsTrackFormRefreshed,
  // Accumulated
  $accumulatedData: $moodsTrackFormAccumulatedData,
  accumulatedDataReset: moodsTrackFormAccumulatedDataReset,
} = moodsTrackFormTagsQueryModel

sample({ clock: TrackFormGate.open, target: moodsTrackFormAvailableSet })
sample({
  clock: moodsTrackFormAvailableSet,
  target: moodsTrackFormRefreshed,
})
sample({
  clock: TrackFormGate.close,
  target: [moodsTrackFormUnavailableSet, moodsTrackFormAccumulatedDataReset],
})

export const $trackFormMoods = combine(
  $moodsTrackFormAccumulatedData,
  $trackFormTrackData,
  (fetchedMoods, track): TrackTagBindModel[] => {
    const combined = [
      ...fetchedMoods.map((a) => ({
        id: a.id,
        name: a.name,
      })),
      ...(track && track.moods ? track.moods : []),
    ]

    const uniqueMap = new Map<number, TrackTagBindModel>()
    for (const tag of combined) {
      uniqueMap.set(tag.id, tag)
    }

    return Array.from(uniqueMap.values())
  },
)

// Albums
export const albumsTrackFormAlbumsQueryModel = createAlbumsQueryModel({
  usePagination: true,
  accumulate: true,
}) as AlbumsQueryModelWithPagination
const {
  availableSet: albumsTrackFormAvailableSet,
  unavailableSet: albumsTrackFormUnavailableSet,
} = albumsTrackFormAlbumsQueryModel
export const {
  //   Query
  query: albumsTrackFormQuery,
  //   Init
  filtersInit: albumsTrackFormFiltersInit,
  // Pagination
  pageIncreased: albumsTrackFormPageIncreased,
  pageSizeChanged: albumsTrackFormPageSizeChanged,
  //   Search
  $search: $albumsTrackFormSearch,
  searchChanged: albumsTrackFormSearchChanged,
  searchReset: albumsTrackFormSearchReset,
  //   Refresh
  refreshed: albumsTrackFormRefreshed,
  // Accumulated
  $accumulatedData: $albumsTrackFormAccumulatedData,
  accumulatedDataReset: albumsTrackFormAccumulatedDataReset,
} = albumsTrackFormAlbumsQueryModel

sample({ clock: TrackFormGate.open, target: albumsTrackFormAvailableSet })
sample({
  clock: TrackFormGate.close,
  target: [albumsTrackFormUnavailableSet, albumsTrackFormAccumulatedDataReset],
})

export const $trackFormAlbums = combine(
  $albumsTrackFormAccumulatedData,
  $trackFormTrackData,
  (fetchedAlbums, track): TrackAlbumBindModel[] => {
    const combined = [
      ...fetchedAlbums.map((a) => ({
        albumId: a.id,
        name: a.name,
        releaseDate: a.releaseDate,
      })),
      ...(track && track.album ? [track.album] : []),
    ]
    const uniqueMap = new Map<string, TrackAlbumBindModel>()
    for (const album of combined) {
      uniqueMap.set(album.albumId, album)
    }

    return Array.from(uniqueMap.values())
  },
)

// Authors

export const $trackFormInitialAuthor = createStore<{
  id: string
  nickname: string
} | null>(null)
  .on(TrackFormGate.open, (_, state) => state.initialAuthor ?? null)
  .on(TrackFormGate.close, () => null)

export const authorsTrackFormAuthorsQueryModel = createAuthorsQueryModel({
  usePagination: true,
  accumulate: true,
}) as AuthorsQueryModelWithPagination

const {
  availableSet: authorsTrackFormAvailableSet,
  unavailableSet: authorsTrackFormUnavailableSet,
} = authorsTrackFormAuthorsQueryModel
export const {
  //   Query
  query: authorsTrackFormQuery,
  //   Init
  filtersInit: authorsTrackFormFiltersInit,
  // Pagination
  $page: $authorsTrackFormPage,
  pageIncreased: authorsTrackFormPageIncreased,
  pageChanged: authorsTrackFormPageChanged,
  pageSizeChanged: authorsTrackFormPageSizeChanged,
  //   Search
  $search: $authorsTrackFormSearch,
  searchChanged: authorsTrackFormSearchChanged,
  searchReset: authorsTrackFormSearchReset,
  //   Refresh
  refreshed: authorsTrackFormRefreshed,
  // Accumulated
  $accumulatedData: $authorsTrackFormAccumulatedData,
  accumulatedDataReset: authorsTrackFormAccumulatedDataReset,
} = authorsTrackFormAuthorsQueryModel

sample({
  clock: TrackFormGate.open,
  target: authorsTrackFormAvailableSet,
})
sample({
  clock: TrackFormGate.close,
  target: [
    authorsTrackFormUnavailableSet,
    authorsTrackFormAccumulatedDataReset,
  ],
})

sample({
  clock: authorsTrackFormAvailableSet,
  target: authorsTrackFormRefreshed,
})

export const $trackFormAuthors = combine(
  $authorsTrackFormAccumulatedData,
  $trackFormTrackData,
  $trackFormInitialAuthor,
  (fetchedCoauthors, track, initialAuthor): TrackAuthorBindModel[] => {
    const combined = [
      ...fetchedCoauthors.map((a) => ({
        id: a.id,
        nickname: a.nickname,
        isCoauthor: false,
      })),
      ...(track ? track.authors.filter((a) => !a.isCoauthor) : []),
      ...(initialAuthor ? [{ ...initialAuthor, isCoauthor: false }] : []),
    ]

    const uniqueMap = new Map<string, TrackAuthorBindModel>()
    for (const author of combined) {
      uniqueMap.set(author.id, author)
    }

    return Array.from(uniqueMap.values())
  },
)

// Coauthors

export const coauthorsTrackFormAuthorsQueryModel = createAuthorsQueryModel({
  usePagination: true,
  accumulate: true,
}) as AuthorsQueryModelWithPagination
const {
  availableSet: coauthorsTrackFormAvailableSet,
  unavailableSet: coauthorsTrackFormUnavailableSet,
} = coauthorsTrackFormAuthorsQueryModel
export const {
  //   Query
  query: coauthorsTrackFormQuery,
  //   Init
  filtersInit: coauthorsTrackFormFiltersInit,
  // Pagination
  pageIncreased: coauthorsTrackFormPageIncreased,
  pageSizeChanged: coauthorsTrackFormPageSizeChanged,
  //   Search
  $search: $coauthorsTrackFormSearch,
  searchChanged: coauthorsTrackFormSearchChanged,
  searchReset: coauthorsTrackFormSearchReset,
  //   Refresh
  refreshed: coauthorsTrackFormRefreshed,
  // Accumulated
  $accumulatedData: $coauthorsTrackFormAccumulatedQueryData,
  accumulatedDataReset: coauthorsTrackFormAccumulatedDataReset,
} = coauthorsTrackFormAuthorsQueryModel

sample({
  clock: TrackFormGate.open,
  target: coauthorsTrackFormAvailableSet,
})
sample({
  clock: TrackFormGate.close,
  target: [
    coauthorsTrackFormUnavailableSet,
    coauthorsTrackFormAccumulatedDataReset,
  ],
})
sample({
  clock: coauthorsTrackFormAvailableSet,
  target: coauthorsTrackFormRefreshed,
})

export const $trackFormCoauthors = combine(
  $coauthorsTrackFormAccumulatedQueryData,
  $trackFormTrackData,
  (fetchedCoauthors, album): TrackAuthorBindModel[] => {
    const combined = [
      ...fetchedCoauthors.map((a) => ({
        id: a.id,
        nickname: a.nickname,
        isCoauthor: false,
      })),
      ...(album ? album.authors.filter((a) => a.isCoauthor) : []),
    ]

    const uniqueMap = new Map<string, TrackAuthorBindModel>()
    for (const author of combined) {
      uniqueMap.set(author.id, author)
    }

    return Array.from(uniqueMap.values())
  },
)

// Form Query Data Combine
sample({
  clock: TrackFormGate.open,
  fn: () => 10,
  target: [
    authorsTrackFormPageSizeChanged,
    coauthorsTrackFormPageSizeChanged,
    tagsTrackFormPageSizeChanged,
    moodsTrackFormPageSizeChanged,
    albumsTrackFormPageSizeChanged,
  ],
})
const $formFieldsQueryData = combine(
  [
    authorsTrackFormQuery.$data,
    coauthorsTrackFormQuery.$data,
    tagsTrackFormQuery.$data,
    moodsTrackFormQuery.$data,
    albumsTrackFormQuery.$data,
  ],
  (data) => data,
)

const $formFieldsQueryStatuses = combine(
  [
    authorsTrackFormQuery.$status,
    coauthorsTrackFormQuery.$status,
    tagsTrackFormQuery.$status,
    moodsTrackFormQuery.$status,
    albumsTrackFormQuery.$status,
  ],
  (statuses) => statuses,
)

export const $isTrackFormDataPending = combine(
  trackByIdQuery.$data,
  trackByIdQuery.$status,
  $formFieldsQueryData,
  $formFieldsQueryStatuses,
  (trackByIdQueryData, trackByIdQueryStatus, otherData, otherStatuses) => {
    if (!trackByIdQueryData && trackByIdQueryStatus === 'initial') return true
    return otherData.some((data, i) => {
      if (otherStatuses[i] === 'initial') return false
      return !data
    })
  },
)

// Modal

export const {
  modalOpened: trackFormModalOpened,
  modalClosed: trackFormModalClosed,
  $isModalOpened: $isTrackFormModalOpened,
} = createModalModel()

const trackFormModalOpenedForEdit = createEvent<{ id: string }>()
sample({
  clock: trackFormModalOpened,
  filter: (params): params is { id: string } => Boolean(params),
  target: trackFormModalOpenedForEdit,
})

sample({
  clock: trackFormModalOpenedForEdit,
  target: trackByIdQuery.start,
})

sample({
  source: TrackFormGate.state,
  clock: trackFormModalClosed,
  fn: (state) => {
    state.resetForm?.()
  },
})

sample({
  source: trackByIdQuery.$data,
  clock: trackFormModalClosed,
  target: trackByIdQuery.reset,
})

// Sumbit

export const trackFormSubmitted = createEvent<UpdateTrackModel>()
export const $trackFormSubmitting = or(
  trackCreate.$pending,
  trackUpdate.$pending,
)

// Create Track

sample({
  source: trackByIdQuery.$status,
  clock: trackFormSubmitted,
  filter: (trackByIdQueryStatus) => trackByIdQueryStatus === 'initial',
  fn: (
    _,
    formData,
  ): {
    data: PutApiTracksIdMutationRequest
  } => {
    return {
      data: formData,
    }
  },
  target: trackCreate.start,
})

// Update Track

sample({
  source: trackByIdQuery.$data,
  clock: trackFormSubmitted,
  filter: (track: TrackModel | null): track is TrackModel => {
    return track !== null && typeof track.id === 'string'
  },
  fn: (track, formData) => {
    return { id: track.id, data: formData }
  },
  target: trackUpdate.start,
})

// OnSubmitted

sample({
  source: TrackFormGate.state,
  clock: [
    trackCreate.finished.success,
    trackUpdate.finished.success,
    trackToggleArchive.finished.success,
  ],
  fn: (gateState, { result }) => {
    if (gateState.onSubmitted) {
      gateState.onSubmitted(result.id)
    }
  },
  target: trackFormModalClosed,
})

sample({
  source: TrackFormGate.state,
  clock: [
    trackCreate.finished.success,
    trackUpdate.finished.success,
    trackToggleArchive.finished.success,
  ],
  target: trackFormModalClosed,
})

const AudioRootNavigation = createEvent()

sample({
  source: TrackFormGate.state,
  clock: [
    trackCreate.finished.success,
    trackUpdate.finished.success,
    trackToggleArchive.finished.success,
  ],
  filter: (state: TrackFormGateProps) => !state.isModal,
  target: AudioRootNavigation,
})

sample({
  clock: AudioRootNavigation,
  fn: () => ({ to: ROUTES.AUDIO.ROOT }),
  target: navigateToFx,
})
